/**
 * OrdersLane — Renders the full order lifecycle on the vehicle scene:
 *
 *   1. ORDER_PLANNED_ON_VEHICLE — dashed/hatched bars (planning intent)
 *   2. ORDER_ON_VEHICLE — solid bars (physically carrying)
 *   3. OrderDelivered — diamond markers (delivery confirmation)
 *
 * Lane 2: Each order gets a deterministic colour from its entity ID.
 * Supports STACKED (overlapping chips) and expanded per-order swimlanes.
 */
import { useMemo } from 'react';
import type {
  OrderOverlay,
  PlannedOrderOverlay,
  DeliveryMarker,
  TimelineSelection,
  TimelineHover,
  ViewMode,
} from '../../../types/vehicleSceneTimeline';
import {
  timeWindowToRect,
  entityColor,
  formatTime,
  formatDuration,
  formatConfidence,
  toMs,
  msToX,
  type TimeScale,
} from '../../../utils/timelineUtils';

interface OrdersLaneProps {
  orders: OrderOverlay[];
  plannedOrders: PlannedOrderOverlay[];
  deliveries: DeliveryMarker[];
  scale: TimeScale;
  viewMode: ViewMode;
  selection: TimelineSelection | null;
  hover: TimelineHover | null;
  onSelect: (edgeId: string) => void;
  onHover: (h: TimelineHover | null) => void;
}

const ROW_HEIGHT = 28;
const ROW_GAP = 2;
const DELIVERY_DIAMOND_SIZE = 8;
const EVIDENCE_STRIPE_WIDTH = 4;

/** Colour for delivery evidence status stripe on the left edge of order bars */
const EVIDENCE_COLORS: Record<string, string> = {
  gps_confirmed: '#10b981',    // green
  graph_confirmed: '#3b82f6',  // blue
  weak: '#f59e0b',             // amber
  missing: '#6b7280',          // gray
  pending: '#8b5cf6',          // purple
  not_applicable: '#6b7280',
};

/** Short label for exit state */
const EXIT_STATE_LABEL: Record<string, string> = {
  delivered: '\u2713',
  on_vehicle: '\u2022\u2022\u2022',
  delivered_after_window: '\u231B',
  cancelled: '\u2717',
};

/** Unified item for row layout computation */
interface LayoutItem {
  id: string;
  fromMs: number;
  toMs: number;
}

/** Compute swimlane rows: stack overlapping items */
function computeRows(items: LayoutItem[]): Map<string, number> {
  const sorted = [...items].sort((a, b) => a.fromMs - b.fromMs);
  const rows = new Map<string, number>();
  const rowEnds: number[] = [];

  for (const item of sorted) {
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] <= item.fromMs) {
        rows.set(item.id, r);
        rowEnds[r] = item.toMs;
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows.set(item.id, rowEnds.length);
      rowEnds.push(item.toMs);
    }
  }
  return rows;
}

export default function OrdersLane({
  orders,
  plannedOrders,
  deliveries,
  scale,
  viewMode,
  selection,
  hover: _hover,
  onSelect,
  onHover,
}: OrdersLaneProps) {
  // Build unified layout items from planned + on-vehicle for row placement
  const allItems = useMemo<LayoutItem[]>(() => {
    const items: LayoutItem[] = [];
    for (const p of plannedOrders) {
      items.push({
        id: `planned:${p.edge_id}`,
        fromMs: toMs(p.from_ts),
        toMs: p.to_ts ? toMs(p.to_ts) : scale.endMs,
      });
    }
    for (const o of orders) {
      items.push({
        id: `carrying:${o.edge_id}`,
        fromMs: toMs(o.from_ts),
        toMs: toMs(o.to_ts),
      });
    }
    return items;
  }, [plannedOrders, orders, scale.endMs]);

  const rows = useMemo(() => computeRows(allItems), [allItems]);
  const maxRow = useMemo(
    () => Math.max(0, ...Array.from(rows.values())),
    [rows],
  );
  const laneHeight =
    viewMode === 'STACKED'
      ? (maxRow + 1) * (ROW_HEIGHT + ROW_GAP) + 8
      : ROW_HEIGHT + 8;

  const isHighlighted = (edgeId: string): boolean => {
    if (!selection) return false;
    if (selection.type === 'order' && selection.id === edgeId) return true;
    return selection.relatedOrderIds?.includes(edgeId) ?? false;
  };

  const isDimmed = (edgeId: string): boolean => {
    if (!selection) return false;
    return !isHighlighted(edgeId);
  };

  // Build a delivery lookup by order entity ID for placing markers
  const deliveriesByOrder = useMemo(() => {
    const map = new Map<string, DeliveryMarker[]>();
    for (const d of deliveries) {
      if (!map.has(d.order_entity_id)) map.set(d.order_entity_id, []);
      map.get(d.order_entity_id)!.push(d);
    }
    return map;
  }, [deliveries]);

  return (
    <div className="vst-lane" data-lane="orders">
      <div className="vst-lane-label">
        <span className="vst-lane-dot" style={{ background: '#3b82f6' }} />
        Orders <span className="vst-lane-badge">lifecycle</span>
      </div>
      <svg className="vst-lane-svg" width={scale.widthPx} height={laneHeight}>
        <defs>
          <pattern id="planned-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.5" />
          </pattern>
        </defs>

        {/* ── Planned orders (dashed/hatched bars) ─────────────────────── */}
        {plannedOrders.map(planned => {
          const fromTs = planned.from_ts;
          const toTs = planned.to_ts ?? new Date(scale.endMs).toISOString();
          const { x, width } = timeWindowToRect(fromTs, toTs, scale);
          const color = entityColor(planned.to_entity_id);
          const layoutKey = `planned:${planned.edge_id}`;
          const row = viewMode === 'STACKED' ? (rows.get(layoutKey) ?? 0) : 0;
          const y = 4 + row * (ROW_HEIGHT + ROW_GAP);
          const highlighted = isHighlighted(planned.edge_id);
          const dimmed = isDimmed(planned.edge_id);

          return (
            <g
              key={layoutKey}
              className="vst-lane-item"
              pointerEvents="auto"
              onClick={() => onSelect(planned.edge_id)}
              onMouseEnter={e =>
                onHover({
                  type: 'order',
                  id: planned.edge_id,
                  relatedSegmentIds: [],
                  relatedOrderIds: [planned.edge_id],
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                })
              }
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Background rect */}
              <rect
                x={x}
                y={y}
                width={width}
                height={ROW_HEIGHT}
                rx={4}
                fill={color}
                opacity={dimmed ? 0.08 : 0.15}
                stroke={highlighted ? '#fff' : color}
                strokeWidth={highlighted ? 2 : 1}
                strokeDasharray="6 3"
              />
              {/* Hatching overlay */}
              <rect
                x={x}
                y={y}
                width={width}
                height={ROW_HEIGHT}
                rx={4}
                fill="url(#planned-hatch)"
                style={{ color }}
                opacity={dimmed ? 0.1 : 0.4}
                pointerEvents="none"
              />
              {/* Label */}
              {width > 60 && (
                <text
                  x={x + 6}
                  y={y + ROW_HEIGHT / 2 + 1}
                  fill={color}
                  fontSize={9}
                  fontStyle="italic"
                  dominantBaseline="middle"
                  pointerEvents="none"
                  className="vst-text-clip"
                  opacity={dimmed ? 0.3 : 0.9}
                >
                  &#9744; {planned.origin_postcode && planned.destination_postcode
                    ? `${planned.origin_postcode}\u2192${planned.destination_postcode}`
                    : planned.source_order_key || planned.to_entity_id.slice(0, 12)}
                </text>
              )}
              <title>
                {`PLANNED: ${planned.source_order_key || planned.to_entity_id}\n${formatTime(toMs(fromTs))} \u2013 ${planned.to_ts ? formatTime(toMs(planned.to_ts)) : 'open'}\nConfidence: ${formatConfidence(planned.confidence)}\nDerivation: ${planned.derivation}\nAuthority: ${planned.authority_level}${planned.origin_postcode ? `\nFrom: ${planned.origin_postcode}` : ''}${planned.destination_postcode ? `\nTo: ${planned.destination_postcode}` : ''}`}
              </title>
            </g>
          );
        })}

        {/* ── On-vehicle orders (solid bars) ───────────────────────────── */}
        {orders.map(order => {
          const { x, width } = timeWindowToRect(order.from_ts, order.to_ts, scale);
          const color = entityColor(order.from_entity_id);
          const layoutKey = `carrying:${order.edge_id}`;
          const row = viewMode === 'STACKED' ? (rows.get(layoutKey) ?? 0) : 0;
          const y = 4 + row * (ROW_HEIGHT + ROW_GAP);
          const highlighted = isHighlighted(order.edge_id);
          const dimmed = isDimmed(order.edge_id);

          // Find delivery markers for this order
          const orderDeliveries = deliveriesByOrder.get(order.to_entity_id) ?? [];

          return (
            <g
              key={layoutKey}
              className="vst-lane-item"
              pointerEvents="auto"
              onClick={() => onSelect(order.edge_id)}
              onMouseEnter={e =>
                onHover({
                  type: 'order',
                  id: order.edge_id,
                  relatedSegmentIds: [],
                  relatedOrderIds: [order.edge_id],
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                })
              }
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Solid carrying bar */}
              <rect
                x={x}
                y={y}
                width={width}
                height={ROW_HEIGHT}
                rx={4}
                fill={color}
                opacity={dimmed ? 0.2 : 0.8}
                stroke={highlighted ? '#fff' : color}
                strokeWidth={highlighted ? 2 : 1}
              />
              {/* Delivery evidence stripe on left edge */}
              {order.delivery_evidence && width > EVIDENCE_STRIPE_WIDTH && (
                <rect
                  x={x}
                  y={y}
                  width={EVIDENCE_STRIPE_WIDTH}
                  height={ROW_HEIGHT}
                  rx={4}
                  fill={EVIDENCE_COLORS[order.delivery_evidence] ?? '#6b7280'}
                  opacity={dimmed ? 0.3 : 1}
                  pointerEvents="none"
                />
              )}
              {/* Exit state badge on right edge */}
              {order.exit_state && width > 30 && (
                <text
                  x={x + width - 14}
                  y={y + ROW_HEIGHT / 2 + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={order.exit_state === 'delivered' ? '#10b981' : order.exit_state === 'on_vehicle' ? '#9ca3af' : '#f59e0b'}
                  fontSize={10}
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  {EXIT_STATE_LABEL[order.exit_state] ?? '?'}
                </text>
              )}
              {/* Label */}
              {width > 80 && (
                <text
                  x={x + (order.delivery_evidence ? EVIDENCE_STRIPE_WIDTH + 4 : 6)}
                  y={y + ROW_HEIGHT / 2 + 1}
                  fill="#fff"
                  fontSize={10}
                  dominantBaseline="middle"
                  pointerEvents="none"
                  className="vst-text-clip"
                >
                  {order.origin_postcode && order.destination_postcode
                    ? `${order.origin_postcode}:${order.destination_postcode}`
                    : order.source_order_key || order.from_entity_id}
                </text>
              )}
              <title>
                {`${order.origin_postcode && order.destination_postcode ? `${order.origin_postcode}:${order.destination_postcode}` : order.label || order.from_entity_id}\nEdge: ${order.edge_id}\n${formatTime(toMs(order.from_ts))} \u2013 ${formatTime(toMs(order.to_ts))} (${formatDuration(order.from_ts, order.to_ts)})\nConfidence: ${formatConfidence(order.confidence)}\nDerivation: ${order.derivation}\nAuthority: ${order.authority_level}${order.origin_postcode ? `\nFrom: ${order.origin_postcode}` : ''}${order.destination_postcode ? `\nTo: ${order.destination_postcode}` : ''}${order.exit_state ? `\nLifecycle: ${order.exit_state}` : ''}${order.delivery_evidence ? `\nDelivery evidence: ${order.delivery_evidence}` : ''}${order.pickup_evidence ? `\nPickup evidence: ${order.pickup_evidence}` : ''}${order.delivery_confidence_score != null ? `\nDelivery confidence: ${formatConfidence(order.delivery_confidence_score)}` : ''}${order.actual_load_hub_name ? `\nLoaded at: ${order.actual_load_hub_name}` : ''}${order.entry_state ? `\nEntry: ${order.entry_state.replace('_', ' ')}` : ''}`}
              </title>

              {/* ── Delivery diamond markers ───────────────────────────── */}
              {orderDeliveries.map(delivery => {
                const deliveryMs = toMs(delivery.occurred_at);
                const deliveryX = msToX(deliveryMs, scale);
                const cy = y + ROW_HEIGHT / 2;
                const s = DELIVERY_DIAMOND_SIZE;
                const diamondColor = delivery.delivery_evidence
                  ? EVIDENCE_COLORS[delivery.delivery_evidence] ?? '#10b981'
                  : '#10b981';

                return (
                  <g key={`delivery:${delivery.event_id}`}>
                    {/* Diamond shape */}
                    <polygon
                      points={`${deliveryX},${cy - s} ${deliveryX + s},${cy} ${deliveryX},${cy + s} ${deliveryX - s},${cy}`}
                      fill={diamondColor}
                      stroke="#fff"
                      strokeWidth={1.5}
                      opacity={dimmed ? 0.3 : 1}
                    />
                    {/* Checkmark inside diamond */}
                    <text
                      x={deliveryX}
                      y={cy + 1}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#fff"
                      fontSize={8}
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      \u2713
                    </text>
                    <title>
                      {`DELIVERED: ${delivery.source_order_key || delivery.order_entity_id}\nAt: ${formatTime(deliveryMs)}\nConfidence: ${formatConfidence(delivery.confidence)}\nAuthority: ${delivery.authority_level}${delivery.destination_postcode ? `\nDestination: ${delivery.destination_postcode}` : ''}${delivery.pod_reference ? `\nPOD: ${delivery.pod_reference}` : ''}`}
                    </title>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* ── Orphan delivery markers (no matching on-vehicle edge) ───── */}
        {deliveries
          .filter(d => !orders.some(o => o.to_entity_id === d.order_entity_id))
          .map(delivery => {
            const deliveryMs = toMs(delivery.occurred_at);
            const deliveryX = msToX(deliveryMs, scale);
            const cy = laneHeight / 2;
            const s = DELIVERY_DIAMOND_SIZE;

            return (
              <g key={`orphan-delivery:${delivery.event_id}`}>
                <polygon
                  points={`${deliveryX},${cy - s} ${deliveryX + s},${cy} ${deliveryX},${cy + s} ${deliveryX - s},${cy}`}
                  fill="#10b981"
                  stroke="#065f46"
                  strokeWidth={1.5}
                  opacity={0.8}
                />
                <text
                  x={deliveryX}
                  y={cy + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fff"
                  fontSize={8}
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  \u2713
                </text>
                <title>
                  {`DELIVERED: ${delivery.source_order_key || delivery.order_entity_id}\nAt: ${formatTime(deliveryMs)}\nConfidence: ${formatConfidence(delivery.confidence)}\nAuthority: ${delivery.authority_level}${delivery.destination_postcode ? `\nDestination: ${delivery.destination_postcode}` : ''}${delivery.pod_reference ? `\nPOD: ${delivery.pod_reference}` : ''}`}
                </title>
              </g>
            );
          })}
      </svg>
    </div>
  );
}
